import { MOCK_NAKSHATRAS } from './homamData';
import cloudinaryVideos from './homamCloudinaryVideos.json';
import localVideos from './localHomamVideos.json';

/**
 * Cloudinary Dynamic Video Optimization Helper
 * Injects on-the-fly streaming optimizations:
 * - q_auto:good / eco: automatically selects the most bandwidth-efficient visual quality
 * - w_{width}: scales to target viewport resolution (no unnecessary 4K/1080p bandwidth waste)
 * - vc_h264: forces universally hardware-accelerated H.264 video codec
 */
export function getOptimizedVideoUrl(url, { width = 540, quality = 'auto:good' } = {}) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cloudinary.com') || !url.includes('/upload/')) return url;
  if (url.includes('/upload/q_') || url.includes('/upload/w_')) return url;

  return url.replace('/upload/', `/upload/q_${quality},w_${width},vc_h264/`);
}

/**
 * Cloudinary Automatic Video Frame Poster Helper
 * Generates an instant, lightweight (~100 KB) preview image from 1 second into the video.
 */
export function getVideoPosterUrl(url, { width = 480 } = {}) {
  return '/images/sai_sahasranama_ayushya_homam_banner.jpg';
}

/**
 * 27 Auspicious Days Homam Videos Data
 * Maps each of the 27 Nakshatras to its dedicated daily video.
 * Supports:
 * 1. High-speed Cloudinary CDN URLs with dynamic streaming optimizations
 * 2. Optimized card preview reels (fast, lightweight)
 * 3. HD modal theater streams
 * 4. Automatic lightweight frame posters
 */
export const ALL_HOMAM_VIDEOS = MOCK_NAKSHATRAS.map((nak, idx) => {
  const dayNum = nak.orderIndex || idx + 1;
  const dayPadded = String(dayNum).padStart(2, '0');

  // Check if Cloudinary CDN URL exists for this day
  const cdnUrl =
    cloudinaryVideos[`day_${dayNum}`] ||
    cloudinaryVideos[`day_${dayPadded}`] ||
    null;

  // Local file mapped path
  const localMapped =
    localVideos[`day_${dayNum}`] ||
    localVideos[`day_${dayPadded}`] ||
    null;

  const hasUploadedVideo = Boolean(localMapped || cdnUrl);
  // Prefer localMapped video first to prevent Cloudinary CDN 401 Unauthorized errors
  const rawVideoSrc = localMapped || cdnUrl || '';

  // Fast preview stream for cards
  const cardVideoSrc = localMapped || (cdnUrl ? getOptimizedVideoUrl(cdnUrl, { width: 480, quality: 'auto:eco' }) : '');

  // HD stream for modal theater player
  const modalVideoSrc = localMapped || (cdnUrl ? getOptimizedVideoUrl(cdnUrl, { width: 720, quality: 'auto:good' }) : '');

  // Frame poster image with fallback (using reliable local image to avoid Cloudinary 401 errors)
  const posterSrc = nak.image || `/images/nakshatras/nakshatra_${dayNum}.jpg`;

  return {
    id: `homam_day_${dayNum}`,
    dayNumber: dayNum,
    dayBadge: `DAY ${dayPadded}`,
    title: `Day ${dayNum}: ${nak.nameEn} Maha Homam`,
    titleTa: `நாள் ${dayNum}: ${nak.nameTa} மகா ஹோமம்`,
    nakshatraEn: nak.nameEn,
    nakshatraTa: nak.nameTa,
    assignedDate: nak.assignedDate,
    deity: nak.deity,
    deityTa: nak.deityTa,
    rulingPlanet: nak.rulingPlanet,
    rulingPlanetTa: nak.rulingPlanetTa,
    symbol: nak.symbol,
    videoSrc: rawVideoSrc,
    cardVideoSrc: cardVideoSrc,
    modalVideoSrc: modalVideoSrc,
    posterSrc: posterSrc,
    cdnUrl: cdnUrl,
    localUrl: localMapped || `/videos/homam/day${dayNum}.mp4`,
    hasCdn: Boolean(cdnUrl),
    hasUploadedVideo: hasUploadedVideo,
    shortDescription: nak.shortDescription,
    introContent: nak.introContent,
    benefits: nak.benefits || []
  };
});

/**
 * Filtered Nakshatra Videos:
 * Strictly includes ONLY Nakshatras that have an authentic video uploaded in videos/homam.
 * Any nakshatras without an uploaded video (e.g. Hasta, Swati, Anusha, Uthiradam, etc.) are excluded.
 */
export const HOMAM_27_VIDEOS = ALL_HOMAM_VIDEOS.filter((item) => item.hasUploadedVideo);

export default HOMAM_27_VIDEOS;
